import BlockIcon from "@mui/icons-material/Block";
import CloseIcon from "@mui/icons-material/Close";
import CommentIcon from "@mui/icons-material/Comment";
import DoneAllIcon from '@mui/icons-material/DoneAll';
import GradingIcon from '@mui/icons-material/Grading';
import GroupsIcon from "@mui/icons-material/Groups";
import IosShareIcon from '@mui/icons-material/IosShare';
import SendIcon from "@mui/icons-material/Send";
import UndoIcon from "@mui/icons-material/Undo";
import WorkIcon from "@mui/icons-material/Work";
import Badge from "@mui/material/Badge";
import IconButton from "@mui/material/IconButton";
import Portal from "@mui/material/Portal";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { keyframes } from "@mui/material";
import { styled } from "@mui/material/styles";
import { ConfirmResponse, useConfirmDialog, } from "app/providers/confirm-dialog";
import { useDialog } from "app/providers/dialog";
import { useAttorneyHubDispatch } from "app/realtime-store/redux-store";
import Loader from "common/components/loader";
import LoadingButton from "common/components/loading-button";
import { AccountType } from "common/values/account-type/account-type";
import Guid from "common/values/guid/guid";
import _ from "lodash";
import { enqueueSnackbar } from "notistack";
import React, { useEffect } from "react";
import { useSession } from "users/session/session-context";
import {
  getCommentThreadsByProposal,
  getIsLoadingCommentThreadsByProposal
} from "work/entities/comment-thread/store/comment-thread-redux-slice";
import AutoCommentGenerator from "work/entities/comment/auto-comment-generator";
import {
  getArePendingComments, publishComments,
  saveDraftComments,
  updateAutoGeneratedComments,
} from "work/entities/comment/store/comments-redux-slice";
import CommentsDrawer from "work/entities/comment/view/comments-drawer";
import ProposalAPIService from "work/entities/proposal/api/proposal-api-service";
import Proposal, { ProposalField, ProposalFieldCategory, } from "work/entities/proposal/proposal";
import ProposalRedline from "work/entities/proposal/redlining/proposal-redline";
import ConflictsTab from "work/entities/proposal/redlining/view/tabs/conflicts-tab";
import DetailsTab from "work/entities/proposal/redlining/view/tabs/details-tab";
import DiscountTab from "work/entities/proposal/redlining/view/tabs/discount-tab";
import FeeScheduleTab from "work/entities/proposal/redlining/view/tabs/fee-schedule-tab";
import PoliciesTab from "work/entities/proposal/redlining/view/tabs/policies-tab";
import TeamTab from "work/entities/proposal/redlining/view/tabs/team-tab";
import {
  addProposal,
  getErrorLoadingProposalById,
  getIsLoadingProposalById,
  getProposalById
} from "work/entities/proposal/store/proposals-redux-slice";
import IssueTraverser from "work/entities/proposal/view/issue-traverser";
import WorkAgreementAPIService from "work/entities/work-agreement/api/work-agreement-api-service";
import { ProposalFieldName } from "work/values/constants";
import ProposalIssues from "work/values/proposal-issues/proposal-issues";
import ProposalIssuesBadge from "work/values/proposal-issues/view/proposal-issues-badge";
import ProposalReviewer from "work/values/proposal-reviewer";
import ReviewerSelector from "work/view/components/reviewer-selector";
import { Masked } from "work/entities/work-agreement/work-agreement";

const pulse = keyframes`
  0% {
    transform: scale(0.9);
    opacity: 0.85;
  }
  50% {
    transform: scale(1.2);
    opacity: 1.0;
  }
  100% {
    transform: scale(0.9);
    opacity: 0.85;
  }
`;
const Header = styled("section")(
  ({ theme }) => ({
    backgroundColor: theme.palette.background.default,
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    paddingBottom: theme.spacing(1),
    position: "sticky",
    top: "0px",
    zIndex: 10,
  }));
const TabsContainer = styled(Tabs)(
  () => ({
    "&.MuiTabs-root": {
      overflow: "hidden",
    },
  }));
const Content = styled("section")(
  ({ theme }) => ({
    alignItems: 'center',
    display: "flex",
    flex: 1,
    flexDirection: "row",
    margin: theme.spacing(
      1,
      0
    ),
  }));
const TabContent = styled("section")(
  ({ theme }) => ({
    alignItems: "stretch",
    flexDirection: "column",
    display: "flex",
    flex: 1,
    height: "25rem",
    margin: theme.spacing(
      2,
      0
    ),
    width: "100%"
  }));
const ActionsContainer = styled("section")(
  ({ theme }) => ({
    [theme.breakpoints.down("md")]: {
      paddingBottom: theme.spacing(1),
      paddingTop: theme.spacing(1),
    },
    backgroundColor: theme.palette.background.default,
    bottom: "0px",
    display: "flex",
    flexDirection: "row",
    paddingBottom: theme.spacing(2.5),
    position: "sticky",
    width: "100%",
    zIndex: 10,
  }));
const ProposalActions = styled("section")(
  ({ theme }) => ({
    [theme.breakpoints.down("md")]: {
      display: "flex",
      flexDirection: "column",
      flexGrow: 0,
      flexWrap: "nowrap",
    },
    alignContent: "center",
    alignItems: "center",
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(15rem, 1fr))",
    gap: theme.spacing(1),
    minHeight: "64px",
    width: "100%",
  }));
const ChangeNavigationContainer = styled("section")(
  ({ theme }) => ({
    [theme.breakpoints.down("md")]: {
      flexDirection: "column",
    },
    alignItems: "center",
    display: "flex",
    justifyContent: "space-between",
    marginBottom: theme.spacing(1),
    width: "100%",
  }));
const ProposalActionButton = styled(LoadingButton)(
  ({ theme }) => ({
    minWidth: theme.spacing(24),
    whiteSpace: "nowrap",
    width: "100%",
    "&.Mui-disabled": {
      color: "rgba(0, 0, 0, 0.26) !important",
      backgroundColor: "rgba(0, 0, 0, 0.12) !important",
    },
  }));
const DialogTab = styled(Tab)(
  () => ({
    "&.MuiTab-root": {
      overflow: "visible",
    },
  }));
const CommentButtons = styled("section")(
  ({ theme }) => ({
    marginLeft: theme.spacing(2),
  }));
const ButtonContainer = styled("div")(
  ({ theme }) => ({
    alignItems: "center",
    display: "flex",
    flexDirection: "column",
    marginBottom: theme.spacing(2),
  }));
const HighlightCommentButton = styled(
  IconButton,
  { shouldForwardProp: (prop) => prop !== 'active' }
)<{ active?: boolean }>(({ active }) => ({
  paddingBottom: 0,
  position: 'relative',
  zIndex: 0,
  ...(active && {
    animation: `${pulse} 1.5s ease-in-out infinite`,
    '&::after': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      backgroundColor: 'rgba(21, 101, 192, 0.05)',
      borderRadius: '50%',
      animation: `${pulse} 1.5s ease-in-out infinite`,
      zIndex: -1,
    },
  }),
}));
const CommentButtonLabel = styled(Typography)(
  () => ({
    fontSize: "0.6em",
  }));
const ReviewerBadge = styled(Badge)(
  () => ({
    marginLeft: '1rem'
  }));
const Fieldset = styled('span')(
  ({ theme }) => ({
    border: "1px solid",
    borderColor: theme.palette.divider,
    borderRadius: theme.shape.borderRadius,
    display: "grid",
    gap: theme.spacing(1),
    gridColumnEnd: "span 2",
    gridTemplateColumns: "repeat(auto-fit, minmax(15rem, 1fr))",
    padding: theme.spacing(1),
    width: "100%",
  }));

type ProposalRedlineProps = {
  proposalId: Guid;
  disableEditing?: boolean;
  navigate: (url: string) => void;
};
export default function ProposalRedlineDialog(
  props: Readonly<ProposalRedlineProps>
) {
  const {
    proposalId,
    navigate,
    disableEditing
  } = props;
  const session = useSession();
  const { closeDialog } = useDialog();
  const confirm = useConfirmDialog();

  const [activeTab, setActiveTab] = React.useState<ProposalFieldCategory>(
    ProposalFieldCategory.Details
  );
  const [currentCommentField, setCurrentCommentField] = React.useState<
    ProposalField | undefined
  >();
  const [commentsPanelTitle, setCommentsPanelTitle] = React.useState<
    string | undefined
  >();
  const [isSaving, setIsSaving] = React.useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = React.useState<boolean>(false);
  const [reviewerSelectorAnchor, setReviewerSelectorAnchor] =
    React.useState<HTMLButtonElement>();
  const [isManagingReviewers, setIsManagingReviewers] =
    React.useState<boolean>(false);
  const [isApprovingReview, setIsApprovingReview] =
    React.useState<boolean>(false);
  const [activeReviewField, setActiveReviewField] =
    React.useState<ProposalField>();
  const [isCommentsPanelOpen, setIsCommentsPanelOpen] =
    React.useState<boolean>(false);

  const proposal = getProposalById(proposalId);
  const isLoadingProposal = getIsLoadingProposalById(proposalId);
  const errorLoadingProposal = getErrorLoadingProposalById(proposalId);
  const commentThreads = getCommentThreadsByProposal(proposal);
  const arePendingComments = getArePendingComments(proposal);
  const isLoadingCommentThreads =
    getIsLoadingCommentThreadsByProposal(proposal);
  const isLoading = isLoadingProposal || isLoadingCommentThreads;
  const [keepCommentsPanelClosed, setKeepCommentsPanelClosed] =
    React.useState<boolean>(false);
  const dispatch = useAttorneyHubDispatch();
  const isReviewing =
    [
      ...(proposal?.clientReviewers ?? []),
      ...(proposal?.vendorReviewers ?? [])
    ]
      .some((reviewer) => reviewer.userId.isEqualTo(session.user?.id)) ?? false;
  const vendorReviewerCount = proposal?.vendorReviewers?.length ?? 0;
  const clientReviewerCount = proposal?.clientReviewers?.length ?? 0;
  const hasReviewers = session.context?.viewingAsVendor
    ? vendorReviewerCount > 0
    : clientReviewerCount > 0;
  const generalFieldUnreadCount = commentThreads?.filter((thread) =>
    thread.field.isEqualTo(ProposalField.General)).reduce((acc, thread) =>
      acc + (thread.numUnread ?? 0), 0) ?? 0;

  /**
   * Sets the active review field to the first issue/comment in the active tab
   */
  useEffect(
    () => {
      if (activeReviewField?.category === activeTab) return;
      const proposalFieldCategories = Object.values(ProposalFieldCategory);
      const proposalFieldNames = Object.values(ProposalFieldName);
      const tabIssues: ProposalField[] = _.uniqBy(
        issues?.entries.map((issue) => issue.field)
          .filter(field => field.category === activeTab) ?? [],
        (field) => field.toJSON()
      );
      const tabUnreadComments: ProposalField[] = _.uniqBy(
        commentThreads?.filter((thread) => thread.numUnread && thread.field.category === activeTab)
          .map((thread) => thread.field) ?? [],
        (field) => field.toJSON()
      );
      const fieldsPendingReview: ProposalField[] = _.chain([...tabUnreadComments, ...tabIssues])
        .uniqBy((field) => `${field.category}-${field.name}-${field.id?.value ?? ''}`)
        .sortBy([
          (field) => proposalFieldCategories.indexOf(field.category),
          (field) => proposalFieldNames.indexOf(field.name)
        ])
        .value();
      setActiveReviewField(fieldsPendingReview[0]);
    },
    [activeTab]
  )

  useEffect(
    () => {
      if (activeReviewField && activeReviewField.category !== activeTab) {
        setActiveTab(activeReviewField?.category ?? activeTab);
      }
    },
    [activeReviewField]
  );

  useEffect(
    () => {
      if (!session?.canSwitchContext || !proposal) return;
      if (
        session.context?.viewingAsVendor &&
        proposal.client?.userId.isEqualTo(session.user?.id)
      ) {
        (async () => {
          try {
            await session.setAccountViewType(AccountType.Client);
          } catch (error) {
            console.error("Error setting account view type:", error);
            enqueueSnackbar("Failed to switch to client account", { variant: "error" });
          }
        })();
      } else if (
        Masked.isNotMasked(proposal.vendorTeam) &&
        (proposal.vendorTeam?.leader?.userId.isEqualTo(session.user?.id) ||
          proposal.vendorTeam?.members?.some((member) => member.userId.isEqualTo(session.user?.id)))
      ) {
        (async () => {
          try {
            await session.setAccountViewType(AccountType.Vendor);
          } catch (error) {
            console.error("Error setting account view type:", error);
            enqueueSnackbar("Failed to switch to vendor account", { variant: "error" });
          }
        })();
      }
    },
    [proposal]
  );

  useEffect(
    () => {
      if (!proposal || proposal.supersededById) return;
      generateChangeAutoComments(proposal);
    },
    [proposal]
  );

  function handleTabChange(
    _event: React.ChangeEvent<object> | null,
    newTab: ProposalFieldCategory
  ) {
    setActiveTab(newTab);
  }

  function handleManageReviewers(event: React.MouseEvent<HTMLButtonElement>) {
    setReviewerSelectorAnchor(event.currentTarget);
    setIsManagingReviewers(true);
  }

  async function handleSubmitRevision() {
    if (!proposal?.redline) return;
    try {
      setIsSubmitting(true);
      dispatch(saveDraftComments(session));
      proposal.redline.clearSessionHistory();
      let message: string;
      const clientReviewers = proposal?.clientReviewers ?? proposal?.clientReviewers ?? [];
      const vendorReviewers = proposal?.vendorReviewers ?? proposal?.vendorReviewers ?? [];
      if ((
        session.context?.viewingAsVendor &&
        vendorReviewers.length &&
        vendorReviewers.length !== vendorReviewers.filter(reviewer => reviewer.dateApproved).length
      ) || (
          !session.context?.viewingAsVendor &&
          clientReviewers.length &&
          clientReviewers.length !== clientReviewers.filter(reviewer => reviewer.dateApproved).length
        )) {
        message = "Not all reviewers have reviewed the changes. Do you still want to submit them to the other" +
          " party?";
      } else {
        message = "Doing so will send all your requested changes for review by the other party.";
      }

      const response = await confirm({
        title: "Send Requested Changes?",
        message: message,
        okButtonText: "Send",
      });

      if (response === ConfirmResponse.Cancel) return;

      const {
        proposal: updatedProposal
      } = await proposal.requestRevision(session);
      proposal.replacedBy = updatedProposal?.id ?? undefined;
      dispatch(addProposal(proposal));
      dispatch(addProposal(updatedProposal));
      enqueueSnackbar(
        "Requested changes sent",
        { variant: "success" }
      );
      navigate('/proposals/active/');
      closeDialog();
    } catch (error) {
      console.error(error);
      enqueueSnackbar(
        `${error}`,
        { variant: "error" }
      );
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleApproveProposal() {
    dispatch(saveDraftComments(session));

    if (!proposal) {
      console.error("No proposal to approve");
      return;
    }
    if (!proposal.redline?.isResolved) {
      console.error(
        "Cannot approve proposal where redline is not resolved."
      );
      return;
    }
    if (!proposal?.isEquivalentTo(proposal.redlinedRevision)) {
      console.error("Cannot approve proposal while there are redlined revision changes.")
    }
    if (!session.user?.id) {
      console.error("No user id to approve proposal");
      return;
    }

    const approveResponse = await confirm({
      title: "Approve Proposal?",
      message:
        "Doing so will approve the proposal and send it to any other parties for their approval.",
      okButtonText: "Approve",
    });

    if (approveResponse === ConfirmResponse.Cancel) {
      setIsSubmitting(false);
      return;
    }

    let updatedProposal: Proposal = proposal;

    try {
      proposal.redline.clearSessionHistory();
      let {
        proposal: updatedProposal
      } = await proposal.approve(session.user.id, session);
      proposal.replacedBy = updatedProposal?.id ?? undefined;
      dispatch(addProposal(proposal));
      dispatch(addProposal(updatedProposal));
    } catch (error) {
      console.error(error);
      enqueueSnackbar(
        "Failed to approve proposal",
        { variant: "error" }
      );
    }

    if (updatedProposal.userCanHire(session.user)) {
      const hireResponse = await confirm({
        title: "Hire Team?",
        message: "Doing so will approve the proposal and hire the vendor-team.",
        okButtonText: "Hire",
      });

      if (hireResponse === ConfirmResponse.Ok) {
        try {
          const {
            proposal: hiredProposal
          } = await updatedProposal.hire(session.user?.id);
          dispatch(addProposal(hiredProposal));
          enqueueSnackbar(
            "Proposal approved and vendor-team hired",
            { variant: "success" }
          );
        } catch (error) {
          console.error(error);
          enqueueSnackbar(
            "Failed to hire vendor-team",
            { variant: "error" }
          );
        }
      }
    } else {
      enqueueSnackbar(
        "Proposal approved",
        { variant: "success" }
      );
    }
  }

  async function handleAccept() {
    if (!proposal) return;

    const acceptResponse = await confirm({
      title: "Accept All Changes?",
      message: "This will mark all changes as accepted.",
      okButtonText: "Accept",
    });

    if (acceptResponse === ConfirmResponse.Cancel) return;

    const updatedRedline = proposal.redline?.clone();
    if (!updatedRedline) return;
    updatedRedline.accept();
    handleProposalRedlineChanged(updatedRedline);
  }

  async function handleReject() {
    if (!proposal) return;
    dispatch(saveDraftComments(session));

    const rejectResponse = await confirm({
      title: "Reject All Changes?",
      message: "This will mark all changes as rejected.",
      okButtonText: "Reject",
    });

    if (rejectResponse === ConfirmResponse.Cancel) return;

    const updatedRedline = proposal.redline?.clone();
    if (!updatedRedline) return;
    updatedRedline.reject();
    handleProposalRedlineChanged(updatedRedline);
  }

  async function handleEndNegotiation() {
    if (!proposal) return;
    dispatch(saveDraftComments(session));

    try {
      const endResponse = await confirm({
        title: "End Negotiation?",
        message: "Doing so will end proposal negotiations.",
        okButtonText: "End Negotiation",
      });

      if (endResponse === ConfirmResponse.Cancel) return;

      setIsSubmitting(true);
      navigate(`/proposals/active/`);
      const {
        proposal: updatedProposal
      } = await proposal.reject(session.user?.id);
      dispatch(addProposal(updatedProposal));
      closeDialog();
    } catch (error) {
      console.error(error);
      enqueueSnackbar(
        "Failed to reject proposal",
        { variant: "error" }
      );
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleCancel() {
    if (!proposal) return;
    dispatch(saveDraftComments(session));

    try {
      const rejectResponse = await confirm({
        title: "Cancel Proposal?",
        message: "Doing so will end proposal negotiations.",
      });

      if (rejectResponse === ConfirmResponse.Cancel) return;

      setIsSubmitting(true);
      navigate(`/proposals/active/`);
      const updatedProposal = await proposal.cancel(session.user?.id);
      dispatch(addProposal(updatedProposal));
      closeDialog();
    } catch (error) {
      console.error(error);
      enqueueSnackbar(
        "Failed to cancel proposal",
        { variant: "error" }
      );
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleHire() {
    if (!proposal) return;
    dispatch(saveDraftComments(session));

    try {
      const reviewers = session.context?.viewingAsVendor
        ? proposal?.vendorReviewers
        : proposal?.clientReviewers;
      let message: string;
      if (
        reviewers.length &&
        reviewers.length !== reviewers.map(reviewer => reviewer.dateApproved).length
      ) {
        message = "Not all reviewers have reviewed the proposal. Do you still want to hire the selected vendor-team?";
      } else {
        message = "Hire the selected vendor-team?"
      }

      const hireResponse = await confirm({
        title: "Hire?",
        message: message,
        okButtonText: "Hire",
      });

      if (hireResponse === ConfirmResponse.Cancel) return;

      setIsSubmitting(true);

      const {
        proposal: updatedProposal
      } = await proposal.hire(session.user?.id);
      dispatch(addProposal(updatedProposal));

      enqueueSnackbar(
        "Proposal hired",
        { variant: "success" }
      );
      closeDialog();
    } catch (error) {
      console.error(error);
      enqueueSnackbar(
        "Failed to hire vendor",
        { variant: "error" }
      );
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleUndoChanges() {
    if (!proposal) return;

    const undoResponse = await confirm({
      title: "Undo All?",
      message: "Doing so will revert all changes.",
      okButtonText: "Undo All",
    });

    if (undoResponse === ConfirmResponse.Cancel) return;

    const updatedRedline = proposal.redline?.clone();
    if (!updatedRedline) return;
    updatedRedline.undo();
    handleProposalRedlineChanged(updatedRedline);
  }

  function shouldDisableTabs(): boolean {
    return isLoading || isSaving || isManagingReviewers;
  }

  function handleProposalRedlineChanged(newProposalRedline: ProposalRedline) {
    if (proposal?.isArchived) return;
    if (!proposal?.redline) {
      throw new Error("Current proposal redline is missing");
    }
    const updatedProposal = proposal.updateRedline(newProposalRedline);
    dispatch(addProposal(updatedProposal));
  }

  function generateChangeAutoComments(updatedProposal: Proposal) {
    if (!updatedProposal?.redline) return;
    const autoComments = new AutoCommentGenerator(
      session,
      updatedProposal,
      undefined,
      commentThreads ?? []
    ).generateAutoCommentsFromRedline();
    dispatch(updateAutoGeneratedComments(autoComments));
  }

  function handleTeamTemplatesUpdated(templateIds: Guid[]) {
    if (!proposal) {
      console.error("No proposal to update vendor-team templates");
      return;
    }
    let updatedProposal: Proposal;
    if (session.context?.viewingAsVendor) {
      updatedProposal = proposal.updateVendorTeamTemplateIds(templateIds);
    } else {
      updatedProposal = proposal.updateClientTeamTemplateIds(templateIds);
    }
    dispatch(addProposal(updatedProposal));
  }

  function handleFeeScheduleTemplatesUpdated(
    templateIds: Guid[],
    newProposalRedline?: ProposalRedline
  ) {
    if (!proposal) {
      console.error("No proposal to update fee schedule templates");
      return;
    }
    let updatedProposal: Proposal;
    if (session.context?.viewingAsVendor) {
      updatedProposal =
        proposal.updateVendorFeeScheduleTemplateIds(templateIds);
    } else {
      updatedProposal =
        proposal.updateClientFeeScheduleTemplateIds(templateIds);
    }
    if (newProposalRedline) {
      updatedProposal = updatedProposal.updateRedline(newProposalRedline);
    }
    dispatch(addProposal(updatedProposal));
  }

  function handleCloseCommentsPanel() {
    setKeepCommentsPanelClosed(true);
    setIsCommentsPanelOpen(false);
    adjustDialogPosition(true);
  }

  async function handleOpenCommentsPanel(
    field?: ProposalField,
    title?: string
  ) {
    if (field) setCurrentCommentField(field);
    if (title) setCommentsPanelTitle(title);
    setIsCommentsPanelOpen(true);
    adjustDialogPosition(false);
  }

  function adjustDialogPosition(panelOpen: boolean) {
    for (const dialog of document.getElementsByClassName("MuiDialog-root")) {
      dialog.setAttribute(
        "style",
        `padding-right: ${panelOpen ? "0px" : "400px"
        }; transition: padding-right 225ms;`
      );
    }
  }

  async function handleMarkReviewedClicked() {
    if (!proposal) throw new Error("No proposal to mark reviewed");

    try {
      const response = await confirm({
        title: "Mark As Reviewed",
        message: "Are you sure you want to mark this proposal as reviewed?",
        okButtonText: "Mark Reviewed",
      });

      if (response === ConfirmResponse.Cancel) return;

      setIsApprovingReview(true);
      const workAgreementService = new WorkAgreementAPIService(session);
      const proposalService = new ProposalAPIService(session, workAgreementService);
      await proposalService.giveReviewerApproval(proposal);
      enqueueSnackbar(
        "Proposal marked as reviewed",
        { variant: "success" }
      )
    } catch (error) {
      console.error(error);
      enqueueSnackbar(
        "Failed to mark proposal as reviewed",
        { variant: "error" }
      );
    } finally {
      setIsApprovingReview(false);
    }
  }

  async function handleSaveAndShareChanges() {
    if (!proposal) {
      throw new Error("No proposal to update");
    }
    if (!hasReviewers) {
      throw new Error("Cannot share proposal without any reviewers");
    }

    const response = await confirm({
      title: "Share",
      message: "Are you sure you want to share any pending comments and/or changes for review?",
      okButtonText: "Share"
    });

    if (response === ConfirmResponse.Cancel) return;

    try {
      setIsSaving(true);

      if (proposal.userCanRedline(session.user)) {
        const sharedProposal = await proposal.share(session);
        if (sharedProposal) {
          dispatch(addProposal(sharedProposal));
        }
      }

      dispatch(publishComments(session));
      enqueueSnackbar(
        "Shared proposal comments/changes",
        { variant: "success" }
      );
    } catch (error) {
      console.error("Failed to share proposal", error);
      enqueueSnackbar("Failed to share proposal", { variant: "error" });
    } finally {
      setIsSaving(false);
    }
  }

  function hasUserApprovedReview(): boolean {
    const reviewers: ProposalReviewer[] = [
      ...(proposal?.clientReviewers ?? []),
      ...(proposal?.vendorReviewers ?? [])
    ];
    return reviewers.some(
      (reviewer) =>
        reviewer.userId.isEqualTo(session.user?.id) && reviewer.dateApproved
    );
  }

  async function handleCommentsClicked(field: ProposalField, name?: string) {
    if (proposal?.id && !keepCommentsPanelClosed) {
      await handleOpenCommentsPanel(field, name);
    } else {
      setCurrentCommentField(field);
      setCommentsPanelTitle(name);
    }
  }

  async function handleTeamMemberQuitProposal(memberId: Guid): Promise<void> {
    if (!proposal?.id || !memberId.isEqualTo(session.user?.id)) return;

    try {
      const workAgreementService = new WorkAgreementAPIService(session);
      const service = new ProposalAPIService(session, workAgreementService);
      await service.quitProposalTeam(proposal.id);
      enqueueSnackbar(
        "You have quit the proposal vendor-team",
        {
          variant: "success",
        }
      );
    } catch (error) {
      console.error(error);
      enqueueSnackbar(
        "Failed to quit proposal vendor-team",
        { variant: "error" }
      );
    }
  }

  function getShareButtonLabel() {
    if (isSubmitting) return "Submitting...";
    if (isSaving) return "Saving...";
    if (!hasReviewers) return "No Reviewers";
    if (arePendingComments && !proposal?.hasUnsavedRedlineChanges) {
      return "Share Comments";
    }
    if (proposal?.hasUnsavedRedlineChanges) {
      return "Share Changes";
    }
    return "Nothing To Share";
  }

  const issues = proposal?.redline
    ? ProposalIssues.fromRedline(proposal?.redline)
    : undefined;
  const manageReviewersDisabled = !proposal ||
    !commentThreads ||
    isSaving ||
    isManagingReviewers ||
    reviewerSelectorAnchor !== undefined ||
    !proposal?.id ||
    isReviewing;
  const reviewers = (session.context?.viewingAsVendor ? proposal?.vendorReviewers : proposal?.clientReviewers) ?? [];
  const totalApprovals = reviewers.filter((reviewer) => reviewer.dateApproved).length;
  const totalReviewers = reviewers.length ?? 0;

  return (!errorLoadingProposal && (
    <>
      <Header>
        <TabsContainer
          variant="scrollable"
          scrollButtons="auto"
          indicatorColor="primary"
          textColor="primary"
          value={activeTab}
          onChange={handleTabChange}
        >
          <DialogTab
            value={ProposalFieldCategory.Details}
            disabled={shouldDisableTabs()}
            label={
              <ProposalIssuesBadge
                issues={issues}
                commentThreads={commentThreads ?? []}
                category={ProposalFieldCategory.Details}
              />
            }
          />
          <DialogTab
            value={ProposalFieldCategory.Team}
            disabled={shouldDisableTabs()}
            label={
              <ProposalIssuesBadge
                issues={issues}
                commentThreads={commentThreads ?? []}
                category={ProposalFieldCategory.Team}
              />
            }
          />
          <DialogTab
            value={ProposalFieldCategory.FeeSchedule}
            disabled={shouldDisableTabs()}
            label={
              <ProposalIssuesBadge
                issues={issues}
                commentThreads={commentThreads ?? []}
                category={ProposalFieldCategory.FeeSchedule}
              />
            }
          />
          <DialogTab
            value={ProposalFieldCategory.Conflicts}
            disabled={shouldDisableTabs()}
            label={
              <ProposalIssuesBadge
                issues={issues}
                commentThreads={commentThreads ?? []}
                category={ProposalFieldCategory.Conflicts}
              />
            }
          />
          <DialogTab
            value={ProposalFieldCategory.Policies}
            disabled={shouldDisableTabs()}
            label={
              <ProposalIssuesBadge
                issues={issues}
                commentThreads={commentThreads ?? []}
                category={ProposalFieldCategory.Policies}
              />
            }
          />
          <DialogTab
            value={ProposalFieldCategory.Discount}
            disabled={shouldDisableTabs()}
            label={
              <ProposalIssuesBadge
                issues={issues}
                commentThreads={commentThreads ?? []}
                category={ProposalFieldCategory.Discount}
              />
            }
          />
        </TabsContainer>
      </Header>
      <Content>
        <TabContent>
          {(isLoading ||
            !proposal?.redline ||
            !proposal.details?.client) && <Loader />}
          {!isLoading &&
            proposal?.redline &&
            proposal.details?.client &&
            (
              <>
                <DetailsTab
                  isOpen={activeTab === ProposalFieldCategory.Details}
                  proposalRedline={proposal.redline}
                  issues={issues}
                  disableEditing={Boolean(disableEditing || (proposal && !proposal?.userCanRedline(session.user)))}
                  activeReviewField={activeReviewField}
                  commentThreads={commentThreads ?? []}
                  onProposalRedlineChange={handleProposalRedlineChanged}
                  onTraverseToNewField={setActiveReviewField}
                  onCommentsClicked={handleCommentsClicked}
                  clientName={proposal.details.client.getFullName()}
                />
                {Masked.isNotMasked(proposal.details.vendorTeam) &&
                  (<TeamTab
                    isOpen={activeTab === ProposalFieldCategory.Team}
                    proposalRedline={proposal.redline}
                    issues={issues}
                    disableEditing={Boolean(disableEditing || (proposal && !proposal?.userCanRedline(session.user)))}
                    activeReviewField={activeReviewField}
                    commentThreads={commentThreads ?? []}
                    onProposalRedlineChange={handleProposalRedlineChanged}
                    onTraverseToNewField={setActiveReviewField}
                    onCommentsClicked={handleCommentsClicked}
                    teamLeader={proposal.details?.vendorTeam?.leader}
                    onTeamTemplatesUpdated={handleTeamTemplatesUpdated}
                    onTeamMemberQuitProposal={handleTeamMemberQuitProposal}
                  />)}
                <FeeScheduleTab
                  isOpen={activeTab === ProposalFieldCategory.FeeSchedule}
                  proposalRedline={proposal.redline}
                  issues={issues}
                  disableEditing={Boolean(disableEditing || (proposal && !proposal?.userCanRedline(session.user)))}
                  activeReviewField={activeReviewField}
                  commentThreads={commentThreads ?? []}
                  onProposalRedlineChange={handleProposalRedlineChanged}
                  onCommentsClicked={handleCommentsClicked}
                  onFeeScheduleTemplatesUpdated={
                    handleFeeScheduleTemplatesUpdated
                  }
                />
                <ConflictsTab
                  isOpen={activeTab === ProposalFieldCategory.Conflicts}
                  proposalRedline={proposal.redline}
                  issues={issues}
                  disableEditing={Boolean(disableEditing || (proposal && !proposal?.userCanRedline(session.user)))}
                  activeReviewField={activeReviewField}
                  commentsToolTip={"Comments"}
                  commentThreads={commentThreads ?? []}
                  onProposalRedlineChange={handleProposalRedlineChanged}
                  onTraverseToNewField={setActiveReviewField}
                  onCommentsClicked={handleCommentsClicked}
                />
                <PoliciesTab
                  isOpen={activeTab === ProposalFieldCategory.Policies}
                  proposalRedline={proposal.redline}
                  issues={issues}
                  disableEditing={Boolean(disableEditing || (proposal && !proposal?.userCanRedline(session.user)))}
                  activeReviewField={activeReviewField}
                  commentsToolTip={"Comments"}
                  commentThreads={commentThreads ?? []}
                  onProposalRedlineChange={handleProposalRedlineChanged}
                  onTraverseToNewField={setActiveReviewField}
                  onCommentsClicked={handleCommentsClicked}
                />
                <DiscountTab
                  isOpen={activeTab === ProposalFieldCategory.Discount}
                  proposalRedline={proposal.redline}
                  issues={issues}
                  disableEditing={Boolean(disableEditing || (proposal && !proposal?.userCanRedline(session.user)))}
                  activeReviewField={activeReviewField}
                  commentsToolTip={"Comments"}
                  commentThreads={commentThreads ?? []}
                  onProposalRedlineChange={handleProposalRedlineChanged}
                  onTraverseToNewField={setActiveReviewField}
                  onCommentsClicked={handleCommentsClicked}
                />
              </>
            )}
        </TabContent>
      </Content>
      <ChangeNavigationContainer>
        <IssueTraverser
          issueFields={
            _.uniqBy(
              issues?.entries.map((issue) => issue.field) ?? [],
              (field) => field.toJSON()
            )
          }
          unreadCommentFields={
            _.uniqBy(
              commentThreads?.filter((thread) => thread.numUnread)
                .map((thread) => thread.field) ?? [],
              (field) => field.toJSON()
            )
          }
          currentField={activeReviewField}
          onTraverseToNewField={setActiveReviewField}
        />
      </ChangeNavigationContainer>
      <ActionsContainer>
        <ProposalActions>
          <Fieldset>
            {isReviewing && (
              <Tooltip title={
                hasUserApprovedReview()
                  ? "Proposal marked as reviewed"
                  : "Mark proposal as reviewed"
              }>
                <span>
                  <ProposalActionButton
                    variant="contained"
                    color="primary"
                    startIcon={<GradingIcon />}
                    loading={isSaving}
                    disabled={
                      isLoading ||
                      isSaving ||
                      isApprovingReview ||
                      isManagingReviewers ||
                      hasUserApprovedReview()
                    }
                    onClick={handleMarkReviewedClicked}
                  >
                    {hasUserApprovedReview() ? "Reviewed" : "Mark Reviewed"}
                  </ProposalActionButton>
                </span>
              </Tooltip>
            )}
            {!isReviewing && (
              <>
                <ProposalActionButton
                  variant="contained"
                  color="primary"
                  startIcon={<GroupsIcon />}
                  endIcon={
                    <ReviewerBadge
                      overlap="rectangular"
                      invisible={
                        manageReviewersDisabled ||
                        !reviewers.length ||
                        (
                          proposal.redline?.isResolved &&
                          proposal.isEquivalentTo(proposal.redlinedRevision)
                        )
                      }
                      badgeContent={
                        <Tooltip
                          title={
                            totalApprovals === totalReviewers
                              ? "All reviewers approved"
                              : "Awaiting reviewer responses"
                          }>
                          <span>
                            <sup>{totalApprovals}</sup>&frasl;<sub>{totalReviewers}</sub>
                          </span>
                        </Tooltip>
                      }
                      color={
                        totalReviewers === totalApprovals
                          ? "success"
                          : "warning"
                      }
                    />
                  }
                  loading={false}
                  disabled={
                    isLoading ||
                    isSaving ||
                    isManagingReviewers ||
                    reviewerSelectorAnchor !== undefined ||
                    !proposal?.id
                  }
                  onClick={handleManageReviewers}
                >
                  Reviewers
                </ProposalActionButton>
                {proposal && (
                  <ReviewerSelector
                    proposal={proposal}
                    popoverAnchor={reviewerSelectorAnchor}
                    onPopoverClose={() => {
                      setReviewerSelectorAnchor(undefined);
                      setIsManagingReviewers(false);
                    }}
                    onReviewersChanged={(updatedReviewers) => {
                      if (session.context?.viewingAsVendor) {
                        dispatch(addProposal(proposal?.updateVendorReviewers(updatedReviewers)));
                      } else {
                        dispatch(addProposal(proposal?.updateClientReviewers(updatedReviewers)));
                      }
                    }}
                    onSend={handleSaveAndShareChanges}
                  />
                )}
              </>
            )}
            <Tooltip title={
              (proposal?.hasUnsavedRedlineChanges || arePendingComments)
                ? "Share redline changes and/or comments for review"
                : "No reviewers or no unshared changes/comments"
            }>
              <span>
                <ProposalActionButton
                  variant="contained"
                  color="primary"
                  startIcon={<IosShareIcon />}
                  loading={isSaving}
                  disabled={
                    (!arePendingComments && !proposal?.hasUnsavedRedlineChanges) ||
                    isSaving ||
                    isSubmitting ||
                    isApprovingReview ||
                    isManagingReviewers ||
                    !hasReviewers
                  }
                  onClick={handleSaveAndShareChanges}
                >
                  {getShareButtonLabel()}
                </ProposalActionButton>
              </span>
            </Tooltip>
          </Fieldset>
          {
            !disableEditing &&
            proposal?.supersedes &&
            !proposal?.userCanHire(session.user) &&
            !proposal.creator?.userId.isEqualTo(session.user?.id) &&
            !proposal.redline?.hasRejections &&
            !proposal.redline?.hasRevisions &&
            !proposal.redline?.isResolved && (
              <>
                <Tooltip title="Accept all changes">
                  <span>
                    <ProposalActionButton
                      variant="contained"
                      color="success"
                      startIcon={<DoneAllIcon />}
                      loading={false}
                      disabled={
                        isSubmitting ||
                        isLoading ||
                        isSaving ||
                        isManagingReviewers
                      }
                      onClick={handleAccept}
                    >
                      Accept All
                    </ProposalActionButton>
                  </span>
                </Tooltip>
                <Tooltip title="Reject all changes">
                  <span>
                    <ProposalActionButton
                      variant="contained"
                      color="error"
                      startIcon={<CloseIcon />}
                      loading={false}
                      disabled={
                        isSubmitting ||
                        isLoading ||
                        isSaving ||
                        isManagingReviewers ||
                        reviewerSelectorAnchor !== undefined ||
                        !proposal?.id ||
                        proposal.creator?.userId.isEqualTo(session.user?.id) ||
                        !proposal.userCanReject(session.user) ||
                        proposal.redline?.isResolved
                      }
                      onClick={handleReject}
                    >
                      Reject All
                    </ProposalActionButton>
                  </span>
                </Tooltip>
              </>
            )}
          {
            !disableEditing &&
            !isReviewing &&
            proposal?.creator?.userId.isEqualTo(session.user?.id) && (
              <ProposalActionButton
                variant="contained"
                color="error"
                startIcon={<CloseIcon />}
                loading={false}
                disabled={
                  isSubmitting ||
                  isLoading ||
                  isSaving ||
                  isManagingReviewers ||
                  reviewerSelectorAnchor !== undefined ||
                  !proposal?.id
                }
                onClick={handleCancel}
              >
                Cancel Proposal
              </ProposalActionButton>
            )}
          {
            proposal?.userCanApprove(session.user) &&
            proposal?.redline?.isResolved &&
            proposal?.isEquivalentTo(proposal.redlinedRevision) &&
            (
              <Tooltip
                title={issues?.entries.length === 0 ? "Submit Approval" : "Resolve Issues to Approve Proposal"}>
                <span>
                  <ProposalActionButton
                    variant="contained"
                    color="success"
                    startIcon={<SendIcon />}
                    loading={isSubmitting}
                    disabled={
                      isSubmitting ||
                      isLoading ||
                      isSaving ||
                      isManagingReviewers ||
                      !proposal?.redline?.isResolved ||
                      (!proposal?.userCanApprove(session.user) && isReviewing) ||
                      issues?.entries.length !== 0
                    }
                    onClick={handleApproveProposal}
                  >
                    Submit Approval
                  </ProposalActionButton>
                </span>
              </Tooltip>
            )}

          {
            !disableEditing &&
            !isReviewing &&
            proposal?.userCanRedline(session.user) && (
              proposal.redline?.hasChanges ||
              (
                proposal.redline?.isResolved &&
                !proposal.isEquivalentTo(proposal.redlinedRevision)
              )
            ) && (
              <Tooltip
                title={
                  issues?.canSubmit ? "Submit Changes" : "Resolve Issues to Submit"
                }
              >
                <span>
                  <ProposalActionButton
                    variant="contained"
                    color="success"
                    startIcon={<SendIcon />}
                    loading={isSubmitting}
                    disabled={
                      isSubmitting ||
                      isLoading ||
                      isSaving ||
                      isManagingReviewers ||
                      !proposal.redline?.isResolved ||
                      (issues && !issues.canSubmit)
                    }
                    onClick={handleSubmitRevision}
                  >
                    Submit Changes
                  </ProposalActionButton>
                </span>
              </Tooltip>
            )}

          {
            !isReviewing &&
            !disableEditing &&
            proposal?.userCanHire(session.user) &&
            proposal?.redline?.sessionHistory.length === 0 && (
              <Tooltip title={!proposal?.userCanHire(session.user) && "Awaiting approval by vendors."}>
                <span>
                  <ProposalActionButton
                    variant="contained"
                    color="success"
                    startIcon={<WorkIcon />}
                    loading={false}
                    disabled={
                      isSubmitting ||
                      isLoading ||
                      isSaving ||
                      isManagingReviewers ||
                      reviewerSelectorAnchor !== undefined ||
                      !proposal?.userCanHire(session.user)
                    }
                    onClick={handleHire}
                  >
                    Hire
                  </ProposalActionButton>
                </span>
              </Tooltip>
            )}
          {!disableEditing && proposal?.redline?.canBeUndone && (
            <ProposalActionButton
              variant="contained"
              color="secondary"
              startIcon={<UndoIcon />}
              loading={false}
              disabled={
                isSubmitting || isLoading || isSaving || isManagingReviewers
              }
              onClick={handleUndoChanges}
            >
              Undo Changes
            </ProposalActionButton>
          )}
          {
            !disableEditing &&
            !isReviewing &&
            !proposal?.creator?.userId.isEqualTo(session.user?.id) && (
              <ProposalActionButton
                variant="text"
                color="error"
                startIcon={<BlockIcon />}
                loading={false}
                disabled={
                  isSubmitting ||
                  isLoading ||
                  isSaving ||
                  isManagingReviewers ||
                  reviewerSelectorAnchor !== undefined ||
                  !proposal?.id
                }
                onClick={handleEndNegotiation}
              >
                End Negotiation
              </ProposalActionButton>
            )}
        </ProposalActions>
        <CommentButtons>
          <ButtonContainer>
            <HighlightCommentButton
              active={activeReviewField?.isEqualTo(ProposalField.General)}
              size="medium"
              color="primary"
              onClick={() => {
                void handleOpenCommentsPanel(
                  ProposalField.General
                );
              }}
            >
              <Badge
                variant={generalFieldUnreadCount ? "standard" : "dot"}
                color="secondary"
                overlap="circular"
                badgeContent={generalFieldUnreadCount > 0 ? generalFieldUnreadCount : undefined}
                invisible={
                  !commentThreads?.some((thread) =>
                    thread.field.isEqualTo(ProposalField.General)
                  )
                }
              >
                <CommentIcon fontSize="medium" />
              </Badge>
            </HighlightCommentButton>
            <CommentButtonLabel variant="button" color="primary">
              Comments
            </CommentButtonLabel>
          </ButtonContainer>
        </CommentButtons>
        {proposal && commentThreads && (
          <Portal>
            <CommentsDrawer
              proposal={proposal}
              commentThreads={commentThreads}
              currentCommentField={currentCommentField}
              activeReviewField={activeReviewField}
              isOpen={isCommentsPanelOpen}
              isSaving={isSaving}
              title={commentsPanelTitle}
              onOpen={() => {
                void handleOpenCommentsPanel();
                setKeepCommentsPanelClosed(false);
              }}
              onClose={handleCloseCommentsPanel}
            />
          </Portal>
        )}
      </ActionsContainer >
    </>
  ));
}
